import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PostCard from '../components/PostCard';
import SearchAndFilter from '../components/SearchAndFilter';
import CreatePostModal from '../components/CreatePostModal';
import { getPosts, canPostToday } from '../api/feedApi';
import useAuthStore from '../../stores/authStore';
import './Feed.css';

const Feed = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ sortBy: 'created_at', order: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [canPost, setCanPost] = useState(true);
  const [postLimitMessage, setPostLimitMessage] = useState('');
  const [hasMore, setHasMore] = useState(true);

  const fetchPosts = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const response = await getPosts({
        ...filters,
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage
      });

      if (response.success) {
        if (append) {
          setPosts(prev => [...prev, ...response.data.posts]);
        } else {
          setPosts(response.data.posts);
        }
        setHasMore(response.data.posts.length === itemsPerPage);
      }
    } catch (err) {
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
    setPosts([]);
    setHasMore(true);
    fetchPosts(1, false);
  }, [fetchPosts]);

  const checkCanPost = useCallback(async () => {
    try {
      const response = await canPostToday();
      if (response.success) {
        setCanPost(response.data.canPost);
        const max = response.data.maxPosts;
        const count = response.data.postCount;
        if (max != null && count != null) {
          setPostLimitMessage(
            `You can create ${max} post${max === 1 ? '' : 's'} per day. You've used ${count} today.`
          );
        } else {
          setPostLimitMessage('');
        }
      }
    } catch (err) {
      console.error('Error checking post limit:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkCanPost();
    }
  }, [isAuthenticated, checkCanPost]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setPosts([]);
    setHasMore(true);
  };

  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPosts(nextPage, true);
    }
  }, [currentPage, loadingMore, hasMore, fetchPosts]);

  useEffect(() => {
    const feedMainElement = document.querySelector('.feed-main');
    if (!feedMainElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = feedMainElement;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMorePosts();
      }
    };

    feedMainElement.addEventListener('scroll', handleScroll);
    return () => feedMainElement.removeEventListener('scroll', handleScroll);
  }, [loadMorePosts]);

  useEffect(() => {
    const sidebarElement = document.querySelector('.feed-news-card');
    if (!sidebarElement) return;

    const handleSidebarScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sidebarElement;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMorePosts(); // Use the same function as main feed
      }
    };

    sidebarElement.addEventListener('scroll', handleSidebarScroll);
    return () => sidebarElement.removeEventListener('scroll', handleSidebarScroll);
  }, [loadMorePosts]);

  const handleCreatePost = () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    if (!canPost) {
      alert(postLimitMessage || 'You have reached your daily post limit. Please try again tomorrow.');
      return;
    }

    setShowCreateModal(true);
  };

  const handlePostSuccess = () => {
    setCurrentPage(1);
    setPosts([]);
    setHasMore(true);
    fetchPosts(1, false);
    checkCanPost();
  };

  const handlePostUpdate = (updatedPost) => {
    setPosts(posts.map(post => 
      post.id === updatedPost.id ? updatedPost : post
    ));
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Recently';
      
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (err) {
      return 'Recently';
    }
  };

  return (
    <div className="feed-page">
      <div className="feed-container">
        {/* Left Sidebar */}
        <aside className="feed-left-sidebar">
          {isAuthenticated && (
            <div className="feed-create-box">
              <button className="create-post-btn" onClick={handleCreatePost}>
                <span className="btn-icon">✏️</span>
                New Post
              </button>
            </div>
          )}
          <SearchAndFilter 
            onFilterChange={handleFilterChange}
            initialFilters={filters}
          />
        </aside>

        {/* Main Feed */}
        <div className="feed-main">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={fetchPosts}>Try Again</button>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📝</span>
              <h3>No posts found</h3>
              <p>Be the first to share something!</p>
              {isAuthenticated && canPost && (
                <button className="create-post-btn" onClick={handleCreatePost}>
                  Create Post
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="posts-list">
                {posts.map(post => (
                  <div key={post.id} id={`post-${post.id}`}>
                    <PostCard 
                      post={post}
                      onUpdate={handlePostUpdate}
                    />
                  </div>
                ))}
              </div>

              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner"></div>
                  <p>Loading more posts...</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Sidebar */}
        <aside className="feed-right-sidebar">
          <div className="feed-news-card">
            <h3 className="feed-news-header">Recent Posts</h3>
            {posts.map(post => (
              <a 
                key={post.id} 
                href={`#post-${post.id}`} 
                className="feed-news-item"
                onClick={(e) => {
                  e.preventDefault();
                  const postElement = document.getElementById(`post-${post.id}`);
                  if (postElement) {
                    postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    postElement.classList.add('highlight-post');
                    setTimeout(() => {
                      postElement.classList.remove('highlight-post');
                    }, 2000);
                  }
                }}
              >
                <h4 className="feed-news-title">{post.title}</h4>
                <div className="feed-news-stats">
                  <span>{formatDate(post.createdAt)}</span>
                  <span>👍 {post.likesCount || 0}</span>
                  <span>👎 {post.dislikesCount || 0}</span>
                  <span>💬 {post.commentsCount || 0}</span>
                  <span className="feed-news-author">• {post.user.full_name || post.user.username}</span>
                </div>
              </a>
            ))}
            {loadingMore && (
              <div className="sidebar-loading-more">
                <div className="spinner"></div>
              </div>
            )}
          </div>
        </aside>
      </div>

      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handlePostSuccess}
      />
    </div>
  );
};

export default Feed;
